import { Component, Input, HostBinding, Output, ViewChild, ElementRef } from "@angular/core";
import { PageComponent, PageService, Shell, sleep, Redirection } from "common";
import { LoginComponent, UserService } from "user";
import { ApiPoll, PollsApi, ApiPostPollsIdVote, ApiUser, ApiPollVote } from "api";
import { BehaviorSubject } from "rxjs";
import { PaywallDialogComponent } from "@tytapp/common-ui/paywall-dialog/paywall-dialog.component";

@Component({
    selector: 'tyt-poll',
    templateUrl: './poll-single.component.html',
    styleUrls: ['./poll-single.component.scss']
})
export class PollSingleComponent extends PageComponent {
    constructor(
        pageService: PageService,
        private userService: UserService,
        private pollsApi: PollsApi,
        private redirection: Redirection
    ) {
        super(pageService);
    }

    _poll: ApiPoll;
    selectedChoice: number;
    voted = false;

    private _pollId: string;

    @Input()
    @HostBinding('class.mini')
    mini: boolean;

    @Input()
    @HostBinding('class.preview')
    preview: boolean;

    @Input()
    get poll() {
        return this._poll;
    }

    get totalVotes() {
        if (!this.poll)
            return 0;

        let voteCount = 0;

        if (!this.poll.questions)
            return 0;

        for (let question of this.poll.questions) {
            voteCount = Math.max(voteCount, question.total_votes);
        }

        return voteCount;
    }

    @Output()
    voteChanged = new BehaviorSubject<ApiPollVote>(null);

    set poll(value) {
        this._poll = value;

        setTimeout(() => this.prepare());
        // if (this.user)
        //   setTimeout(() => this.fetchPoll());
    }

    get pollId() {
        if (!this._poll)
            return undefined;

        return this._poll.id;
    }

    get opensLater() {
        if (this.poll && this.poll.opens_at) {
            if (new Date(this.poll.opens_at).getTime() > Date.now())
                return true;
        }

        return false;
    }

    get closedEarlier() {
        if (this.poll && this.poll.closes_at) {
            if (new Date(this.poll.closes_at).getTime() < Date.now())
                return true;
        }

        return false;
    }

    get openForVoting() {
        if (!this.poll)
            return false;

        if (this.poll.opens_at) {
            if (new Date(this.poll.opens_at).getTime() > Date.now())
                return false;
        }

        if (this.poll.opens_at) {
            if (new Date(this.poll.opens_at).getTime() > Date.now())
                return false;
        }

        if (this.poll.closes_at) {
            if (new Date(this.poll.closes_at).getTime() < Date.now())
                return false;
        }

        return true;


    }
    get commentIdentifier() {
        if (!this.poll)
            return null;
        return `poll_${this.poll.slug}`;
    }

    privacyMode = 'none';
    user: ApiUser;
    async init() {

        if (await this.shell.hasFeature('apps.web.petition_privacy_show_email'))
            this.privacyMode = 'showEmail';
        else if (await this.shell.hasFeature('apps.web.petition_privacy_assurance'))
            this.privacyMode = 'assurance';

        this.subscribe(this.userService.userChanged, user => {
            this.user = user;
            // if (this._poll)
            //   this.fetchPoll();
        });
    }

    showResultsWithoutVoting() {
        this.showResultsWithoutVote = true;
        this.showResults();
    }

    reloading = false;
    resultsReady = false;
    savedChoice;

    async reload() {
        if (this.reloading)
            return;

        this.reloading = true;
        await sleep(1000);
        await this.fetchPoll();
        this.reloading = false;
    }

    fakeVotes = false;

    async fetchPoll() {
        this._poll = await this.pollsApi.getPoll(this.pollId).toPromise();
        if (this._poll)
            this.prepare();
    }

    async prepare() {
        if (this.fakeVotes) {
            for (let question of this.poll.questions) {
                let totalVotes = 0;
                for (let choice of question.choices) {
                    let count = Math.floor(Math.random() * 10000);
                    choice.votes_count = `${count}`;
                    totalVotes += count;
                }

                question.total_votes = totalVotes;
            }
        }

        this.voteChanged.next(this.userVote);

        if (this.userVote) {
            this.selectedChoice = this.userVote.choice_ids[0];
            this.voted = true;
        }

        if (!this.openForVoting && !this.opensLater)
            this.showResultsWithoutVote = true;

        this.showResults();

        if (typeof window !== 'undefined') {
            setTimeout(() => {
                if (!this.descriptionBox)
                    return;

                let el = this.descriptionBox.nativeElement;
                let maxPx = parseInt(getComputedStyle(el).maxHeight);
                this.showReadMore = (el.clientHeight >= maxPx - 10);
            });
        }
    }

    showShareOptions = false;
    showResultsWithoutVote = false;

    showResults() {
        if (typeof window !== 'undefined') {
            setTimeout(() => this.resultsReady = true, 1000);
        } else {
            this.resultsReady = true;
        }
    }

    changeVote() {
        this.voted = false;
        this.savedChoice = this.selectedChoice;
    }

    cancelChangeVote() {
        this.voted = true;
        this.selectedChoice = this.savedChoice;
    }

    get userVote() {
        if (!this.poll || !this.poll.questions || this.poll.questions.length === 0)
            return null;

        return this.poll.questions[0].vote ? this.poll.questions[0].vote : null;
    }

    selectChoice(choice: number) {
        if (!this.voted) {
            this.selectedChoice = choice;
        }
    }

    async vote() {
        if (this.voted)
            return;

        let voteNoun = 'vote';
        let voteVerb = 'vote';
        let pollNoun = 'poll';
        let sourcePrefix = 'poll';

        // Petitions use different verbiage

        if (this.poll.type === 'Petition') {
            voteNoun = 'signature';
            voteVerb = 'sign';
            pollNoun = 'petition';
            sourcePrefix = 'petition';

            // If it is a Petition the user is not allowed to sign it from the Home Page, we are redirecting to the petitions page
            this.redirection.go(`/petitions/${this.poll.slug}`);
            return;
        }

        // More copy based on the above verbiage

        let source = this.pollId ? `${sourcePrefix}:${this.pollId}` : undefined;
        let genericErrorMessage =
            `Error: Could not record your ${voteNoun}! `
            + `Please try again and if the issue persists, `
            + `contact support at support@tytnetwork.com`
            ;

        this.resultsReady = false;
        await this.userService.ready;

        let user = this.userService.user;
        if (!user) {
            this.userService.source = source;
            this.shell.showDialog(LoginComponent, {
                reason: `Log in to ${voteVerb}`
            });
            return;
        }

        if (this.poll.membership_required && !user.entitled) {
            this.userService.source = source;
            this.shell.showDialog(PaywallDialogComponent, { noun: pollNoun });
            return;
        }

        if (!this.voted) {

            let result: ApiPoll;

            if (this.poll.type === 'Petition') {
                result = await this.pollsApi.signPetition(this.poll.slug).toPromise();
                if (result) {
                    this.voted = true;
                }
            } else {
                const bodyPayload: ApiPostPollsIdVote = {
                    poll_question_id: this.poll.questions[0].id,
                    choice_ids: [this.selectedChoice].join(',')
                };

                let result: ApiPoll;

                try {
                    result = await this.pollsApi.pollVote(this.poll.slug, bodyPayload).toPromise();
                } catch (e) {
                    if (e.json)
                        e = e.json();
                    alert(e.message || e.error || genericErrorMessage);
                }

                if (result) {
                    this.voted = true;
                }
            }

            await this.fetchPoll();
            this.showResults();
        }
    }

    identity(item) {
        return item.id;
    }

    getPercentage(count, total) {
        if (total === 0)
            return 0;

        return Math.round(((count * 100) / total) * 100) / 100.0;
    }

    async removeVote() {
        await this.pollsApi.clearVote(this.poll.slug).toPromise();
        await this.fetchPoll();
    }

    get isVoteValid() {
        if (!this.poll)
            return false;

        if (this.poll.type === 'Petition')
            return true;

        return this.selectedChoice && this.openForVoting;
    }

    get votedVerb() {
        if (this.poll && this.poll.type === 'Petition')
            return 'Signed';

        return 'Voted';
    }

    get voteVerb() {
        if (this.poll && this.poll.type === 'Petition')
            return 'Sign this petition';

        return 'Vote';
    }

    get votesLabel() {
        if (this.poll && this.poll.type === 'Petition')
            return 'signatures';

        return 'votes';
    }

    get showChoices() {
        if (this.poll && this.poll.type === 'Petition')
            return false;
        return true;
    }

    get votingOpenLabel() {
        if (this.poll && this.poll.type === 'Petition')
            return 'Sign Now';

        return 'Voting Open';
    }

    get showDescription() {
        if (this.poll && this.poll.type === 'Petition') {
            return true;
        }

        return this.preview || !this.mini;
    }

    get useShortDescription() {
        return this.mini;
    }

    @ViewChild('descriptionBox')
    descriptionBox: ElementRef<HTMLElement>;

    showReadMore = false;

    get pollUrl() {
        if (!this.poll)
            return undefined;

        if (this.poll.type === 'Petition')
            return `/petitions/${this.poll.slug}`;

        return `/polls/${this.poll.slug}`;
    }
}
